import { bildName, bildPlan } from "../pages/kommunikation/nachrichten/model";

/**
 * K-529 d: Bilder vor dem Hochladen im Browser verkleinern (längste Kante 2000 px, JPEG bzw. WebP). Fotos vom Handy haben oft
 * 4000 px und 5 bis 12 MB; im Chat reicht weit weniger. Was der Browser nicht lesen kann (HEIC außerhalb von Safari), bleibt
 * unverändert. Wird das Ergebnis nicht kleiner, bleibt ebenfalls das Original. Ohne Canvas (Server, Tests) unverändert.
 */
export async function verkleinereBild(file: File): Promise<File> {
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") return file;
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return file;
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file);
  } catch {
    return file;
  }
  try {
    const plan = bildPlan(file, bmp.width, bmp.height, webpMoeglich());
    if (!plan) return file;
    const canvas = document.createElement("canvas");
    canvas.width = plan.width;
    canvas.height = plan.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    // JPEG kennt keine Transparenz: weißer Grund statt schwarzer Flächen
    if (plan.type === "image/jpeg") {
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, plan.width, plan.height);
    }
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bmp, 0, 0, plan.width, plan.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, plan.type, plan.quality));
    if (!blob || blob.size === 0 || blob.type !== plan.type || blob.size >= file.size) return file;
    return new File([blob], bildName(file.name, plan.type), { type: plan.type, lastModified: file.lastModified });
  } finally {
    bmp.close();
  }
}

let webp: boolean | null = null;
/** Kann der Browser WebP schreiben? (Safari erst seit 2023; sonst JPEG bzw. PNG) */
function webpMoeglich(): boolean {
  if (webp !== null) return webp;
  try {
    const c = document.createElement("canvas");
    c.width = c.height = 1;
    webp = c.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    webp = false;
  }
  return webp;
}
